import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from 'date-fns'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'

import AnalogClock from '../components/AnalogClock'
import { listEvents, CalEvent } from '../lib/api'

export default function Calendar() {
  const [cursor, setCursor] = useState(new Date())

  const monthStart = startOfMonth(cursor)
  const monthEnd = endOfMonth(cursor)
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })

  const { data } = useQuery({
    queryKey: ['events', gridStart.toISOString(), gridEnd.toISOString()],
    queryFn: () => listEvents(gridStart.toISOString(), gridEnd.toISOString()),
  })

  const events = data?.events || []
  const eventsByDay = useMemo(() => groupByDay(events), [events])

  const days: Date[] = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d)

  const now = new Date()

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-5 flex items-center gap-4">
        <div>
          <div className="h-eyebrow">Planner</div>
          <h1 className="h-section">{format(cursor, 'MMMM yyyy')}</h1>
        </div>
        <div className="flex gap-1 ml-4">
          <button onClick={() => setCursor(subMonths(cursor, 1))} className="pill-ghost">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setCursor(new Date())} className="pill-ghost">Today</button>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="pill-ghost">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex-1" />
        <Link to="/calendar/new" className="pill-primary">
          <Plus size={16} strokeWidth={2} />
          New event
        </Link>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-[280px_1fr] gap-4 p-4 overflow-hidden">
        {/* Left: analog clock + at-a-glance today */}
        <aside className="flex flex-col gap-4 overflow-auto">
          <div className="card-inset p-4">
            <div className="flex items-center gap-4">
              <AnalogClock size={104} />
              <div>
                <div className="text-2xl font-semibold text-ink-900">{format(now, 'h:mmaaa')}</div>
                <div className="text-xs text-ink-500 mt-1">{format(now, 'EEE, do MMMM')}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="h-eyebrow mb-2 px-1">Today's schedule</div>
            {(() => {
              const todayEvents = eventsByDay[format(now, 'yyyy-MM-dd')] || []
              if (todayEvents.length === 0) {
                return <div className="card-inset p-4 text-sm text-ink-500">Nothing scheduled.</div>
              }
              return (
                <ul className="space-y-2">
                  {todayEvents.map(e => (
                    <li key={e.uid}>
                      <Link
                        to={`/calendar/${encodeURIComponent(e.uid)}?dtstart=${encodeURIComponent(e.dtstart)}`}
                        className="card p-3 block hover:shadow-lift transition-shadow"
                      >
                        <div className="text-xs text-pulse mb-1 font-medium">
                          {e.all_day ? 'All day' : format(new Date(e.dtstart), 'HH:mm')}
                        </div>
                        <div className="text-sm font-medium text-ink-900">
                          {e.summary || '(untitled)'}
                        </div>
                        {e.location && (
                          <div className="text-xs text-ink-500 mt-1">{e.location}</div>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )
            })()}
          </div>
        </aside>

        {/* Right: month grid */}
        <section className="card-inset overflow-hidden flex flex-col">
          <div className="grid grid-cols-7 border-b border-line/60 text-[10px] font-medium text-ink-300 uppercase tracking-widest">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <div key={d} className="py-2.5 text-center">{d}</div>
            ))}
          </div>

          <div className="flex-1 grid grid-cols-7 grid-rows-6 gap-px bg-line/40 overflow-hidden">
            {days.map(day => {
              const key = format(day, 'yyyy-MM-dd')
              const dayEvents = eventsByDay[key] || []
              const inMonth = isSameMonth(day, cursor)
              const isToday = isSameDay(day, now)
              return (
                <div key={key} className={`p-2 text-xs ${inMonth ? 'bg-surface' : 'bg-canvas text-ink-300'} min-w-0`}>
                  <div className="flex justify-end">
                    <span className={`
                      inline-flex items-center justify-center rounded-md text-[11px] leading-none
                      ${isToday ? 'w-6 h-6 bg-accent text-raised font-semibold' : 'w-6 h-6 text-ink-700'}
                    `}>
                      {format(day, 'd')}
                    </span>
                  </div>
                  <ul className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map(e => (
                      <li key={e.uid}>
                        <Link
                          to={`/calendar/${encodeURIComponent(e.uid)}?dtstart=${encodeURIComponent(e.dtstart)}`}
                          className="block truncate rounded-md px-1.5 py-0.5 text-[11px] text-ink-900 bg-white/70 hover:bg-white"
                          title={e.summary}
                        >
                          {!e.all_day && (
                            <span className="text-pulse mr-1 font-medium">{format(new Date(e.dtstart), 'HH:mm')}</span>
                          )}
                          {e.summary || '(untitled)'}
                        </Link>
                      </li>
                    ))}
                    {dayEvents.length > 3 && (
                      <li className="text-[11px] text-ink-500 px-1.5">+{dayEvents.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}

function groupByDay(events: CalEvent[]): Record<string, CalEvent[]> {
  const out: Record<string, CalEvent[]> = {}
  for (const e of events) {
    const key = format(new Date(e.dtstart), 'yyyy-MM-dd')
    ;(out[key] ||= []).push(e)
  }
  return out
}
